// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useState } from 'preact/hooks';
import {
  useSession,
  EmailHasLocalAccountError,
  Web3AuthEmailRequiredError,
  Web3AuthLossAckRequiredError,
} from '../session-context.js';

/** The sign-in methods offered by [`LoginMethodChooser`]. `local` and
 *  `hardware` (HW.3) route through `onSelect` to their own panels; web3auth
 *  goes via `onWeb3Auth` (it opens a modal, no panel of its own).
 *  `web3auth-email` (COB-345) and `web3auth-ack` (COB-272) are not
 *  user-selectable methods — the hook routes to them when a provider-less
 *  Web3Auth sign-in needs an email captured / a new provider-email account
 *  needs the login-loss acknowledgement, mirroring how a collision routes
 *  to `local`. */
export type LoginMethod =
  | 'local'
  | 'hardware'
  | 'web3auth'
  | 'web3auth-email'
  | 'web3auth-ack';

/** Shared login-method state + the Web3Auth handler, used by every page that
 *  offers sign-in inline (the home page and the shared-vault accept landing —
 *  COB-220). Extracting the handler keeps the Web3Auth / one-account-per-email
 *  collision logic in exactly one place so the two surfaces can't diverge.
 *
 *  `handleWeb3Auth` opens the Web3Auth PnP modal; on the one-account-per-email
 *  collision (the social email already owns a local passcode account) it bounces
 *  to the Self Custody (`local`) flow with the email prefilled rather than
 *  surfacing a raw 409. A brand-new provider-email account routes to the
 *  login-loss acknowledgement panel (`web3auth-ack`, COB-272) before its
 *  deferred register fires. An external-wallet (MetaMask tile) connect — which
 *  yields no exportable key — surfaces `ExternalWalletNotSupportedError`, whose
 *  message IS the honest routing copy; it flows through the generic error arm
 *  below onto all three chooser surfaces (home, shared-vault accept,
 *  sign-verifier) as user-facing text (COB-348, the established COB-345
 *  message-as-copy pattern). Pages render `LoginMethodChooser` while
 *  `loginMethod === null` and the local `Onboarding` while it is `'local'`. */
export function useWeb3AuthLogin() {
  const session = useSession();
  const [loginMethod, setLoginMethod] = useState<LoginMethod | null>(null);
  const [web3authBusy, setWeb3authBusy] = useState(false);
  const [web3authError, setWeb3authError] = useState<string | null>(null);
  // When a web3auth email collides with an existing local account, we bounce to
  // the Self Custody login with the email prefilled (one email = one account).
  const [collisionEmail, setCollisionEmail] = useState<string | null>(null);

  const handleWeb3Auth = async () => {
    setWeb3authError(null);
    setWeb3authBusy(true);
    try {
      await session.loginWithWeb3Auth();
    } catch (e) {
      if (e instanceof EmailHasLocalAccountError) {
        // Email already belongs to another account — route to Self Custody
        // login with the email prefilled instead of a raw 409. The copy is
        // method-neutral (COB-345): the holder need not be a passcode account,
        // but Self Custody is where a passcode holder can sign in.
        setCollisionEmail(e.email);
        setLoginMethod('local');
        return;
      }
      if (e instanceof Web3AuthEmailRequiredError) {
        // COB-345: the sign-in worked but yielded no email — route to the
        // capture panel to collect one (the live agent is stashed in the
        // session ref for `completeWeb3AuthEmailRegistration`).
        setLoginMethod('web3auth-email');
        return;
      }
      if (e instanceof Web3AuthLossAckRequiredError) {
        // COB-272: a brand-new account with a provider email — the register
        // is deferred behind the login-loss acknowledgement panel (the live
        // agent + register parameters are stashed in the session refs for
        // `completeWeb3AuthRegistration`).
        setLoginMethod('web3auth-ack');
        return;
      }
      setWeb3authError(e instanceof Error ? e.message : 'Web3Auth login failed');
    } finally {
      setWeb3authBusy(false);
    }
  };

  return {
    loginMethod,
    setLoginMethod,
    web3authBusy,
    web3authError,
    collisionEmail,
    setCollisionEmail,
    handleWeb3Auth,
  };
}

/** The first-impression sign-in chooser: Cobault Self Custody (browser key
 *  agent) as the primary CTA, MetaMask Embedded Wallets (Web3Auth) as the
 *  delegated-custody alternative, and hardware (Ledger — HW.3) as the
 *  keys-never-in-browser option. Rendered by the home page and the
 *  shared-vault accept landing — pair it with [`useWeb3AuthLogin`] for the
 *  state + handler. */
export function LoginMethodChooser({
  onSelect,
  onWeb3Auth,
  web3authBusy,
  web3authError,
}: {
  onSelect: (m: LoginMethod) => void;
  onWeb3Auth: () => void;
  web3authBusy: boolean;
  web3authError: string | null;
}) {
  return (
    <div class="login-chooser login-chooser--minimal">
      <h2 class="login-chooser-title">Sign up or sign in</h2>
      <button
        type="button"
        class="login-chooser-primary"
        onClick={() => onSelect('local')}
      >
        <span>
          Cobault Self Custody
          <span class="login-chooser-primary-sub">
            {' '}(email and passcode, local keys)
          </span>
        </span>
        <span class="login-chooser-primary-arrow" aria-hidden="true">→</span>
      </button>

      <details class="login-chooser-details">
        <summary class="login-chooser-summary">How Cobault Self Custody works</summary>
        <div class="login-chooser-details-body">
          <p>
            Cobault generates a 24-word BIP-39 seed phrase locally in
            your browser when you first sign up. The seed lives in an
            isolated Web Worker — an in-browser key agent — that signs
            every Bitcoin transaction without sending the seed or any
            derived private key to Cobault.
          </p>
          <p>
            You unlock the wallet on subsequent sign-ins with email
            and a passcode. Cobault stores an Argon2id verifier of
            the passcode (not the passcode itself), proving you know
            it without ever learning it.
          </p>
          <p>
            If you lose this device <em>and</em> the seed phrase,
            Cobault cannot recover your wallet. Back up the 24 words
            somewhere safe — this is the trade-off for true
            self-custody.
          </p>
        </div>
      </details>

      <button
        type="button"
        class="login-chooser-primary"
        onClick={onWeb3Auth}
        disabled={web3authBusy}
        aria-busy={web3authBusy}
      >
        {/* The "W" logomark from the official web3auth-by-MetaMask lockup
            (their preferred mark — the kit retired the fox), rendered black
            like the Ledger mark so it tracks the ink-on-gold tile text; the
            label is set in our own type so all three method tiles read as one
            family. Decorative: named by the adjacent tile text, like Ledger's.
            COB-168: the label stays identical in both states — only the
            trailing arrow swaps to a spinner — so the centred button text
            never re-centres / jumps when it starts connecting. */}
        <img
          class="login-chooser-mark"
          src="/web3auth-mark.svg"
          alt=""
          aria-hidden="true"
          width={20}
          height={20}
        />
        <span>
          web3auth
          <span class="login-chooser-primary-sub">{' '}(social login)</span>
        </span>
        <span class="login-chooser-primary-arrow" aria-hidden="true">
          {web3authBusy ? <span class="login-chooser-primary-spinner" /> : '→'}
        </span>
      </button>
      {web3authError && <p class="offer-error">{web3authError}</p>}

      <details class="login-chooser-details">
        <summary class="login-chooser-summary">How web wallet works</summary>
        <div class="login-chooser-details-body">
          <p>
            Sign in with a social account (Google, X, Facebook…) or your
            email — there is no seed phrase to create or write down. Your
            Bitcoin key is created and recovered through MetaMask Embedded
            Wallets (Web3Auth), which splits it into shares held across
            your login, this device, and the Web3Auth/MetaMask network, so
            no single party — Cobault included — ever holds the whole key.
          </p>
          <p>
            To sign a Bitcoin transaction the shares are combined into your
            key inside your browser, used to sign, then discarded — the key
            is never sent to Cobault or any Cobault server. On your next
            visit you just log in again with the same provider; there is no
            24-word phrase to restore.
          </p>
          <p>
            The trade-off is a different trust model: instead of a seed only
            you hold, your key's safety rests on your login provider and the
            Web3Auth/MetaMask network. Your deposit stays protected by the
            2-of-3 multisig regardless — this key is only one of the three,
            so a problem with your login alone cannot move your funds.
            Losing the login permanently does mean losing your signing key,
            though — Cobault cannot rebuild it — so keep the login account
            itself recoverable.
          </p>
        </div>
      </details>

      <button
        type="button"
        class="login-chooser-primary"
        onClick={() => onSelect('hardware')}
      >
        <img
          class="login-chooser-mark"
          src="/ledger-mark.svg"
          alt=""
          aria-hidden="true"
          width={20}
          height={20}
        />
        <span>
          Hardware wallet
          <span class="login-chooser-primary-sub">{' '}(Ledger, keys on-device)</span>
        </span>
        <span class="login-chooser-primary-arrow" aria-hidden="true">→</span>
      </button>

      <details class="login-chooser-details">
        <summary class="login-chooser-summary">How hardware sign-in works</summary>
        <div class="login-chooser-details-body">
          <p>
            Connect a Ledger over USB and sign Cobault transactions on the
            device's screen, the way hardware wallets are designed to work —
            your keys never touch this browser's memory. Every vault
            withdrawal and wallet payment shows its destination and amount
            on the device before it signs. BitBox02 support is coming later.
          </p>
          <p>
            Needs a WebHID-capable browser (Chrome or Edge). For now a
            hardware account lives on the browser you register it in —
            signing in from a new browser with the same device creates a
            fresh account, until device-native sign-in arrives.
          </p>
        </div>
      </details>

      {/* Nominative-use trademark notice for the brand marks on the
          web-wallet / Ledger tiles: the marks indicate wallet compatibility
          only and carry no claim of endorsement. Names every mark actually
          rendered above — the "web3auth by MetaMask" lockup and the Ledger
          mark. Rendered once here so it rides along on every surface that
          mounts the chooser. */}
      <p class="login-chooser-trademark">
        Web3Auth and MetaMask are trademarks of Consensys Software Inc.
        Ledger is a trademark of Ledger SAS. Marks indicate compatibility
        only; no endorsement is claimed or implied.
      </p>

      {/* F12.A: unauth'd recovery path for users who lost both passcode
          and seed. Deliberately understated — a prominent link here
          would encourage mis-clicks. */}
      <p class="login-chooser-lost-access">
        <a href="/account/abandon">Lost access to your account?</a>
      </p>
    </div>
  );
}
